import styled, { keyframes } from 'styled-components';

import { media } from '../../utils/mediaQueries';

const slideIn = keyframes`
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(100%);
  }
`;

export const Flag = styled.img`
  display: flex;
  align-items: center;
  justify-content: center;
  object-fit: cover;
`;

export const TriggerWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;

export const PreviewLabelText = styled.span`
  ${media.mobile} {
    display: none;
  }
`;

export const PreviewLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 400;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#9ca3af' : '#6b7280')};
  white-space: nowrap;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    left: 22px;
    right: 0;
    bottom: -2px;
    height: 1px;
    background: ${({ theme }) => (theme.mode === 'dark' ? '#9ca3af' : '#6b7280')};
    transform: scaleX(0);
    transform-origin: right;
    transition: transform 0.2s ease;
  }

  ${TriggerWrapper}:hover &::after {
    transform: scaleX(1);
    transform-origin: left;
  }
`;

export const PreviewFlagWrapper = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export const ChangeLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#9ca3af' : '#6b7280')};
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
  transition: color 0.2s ease;

  &:hover {
    color: #1a4d4d;
  }
`;

export const TriggerButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 30px;
  padding: 0 10px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#e5e7eb')};
  background: ${({ theme }) => (theme.mode === 'dark' ? '#242a33' : '#ffffff')};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => (theme.mode === 'dark' ? '#4b5563' : '#d1d5db')};
    background: ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#f9fafb')};
  }

  &:active {
    transform: scale(0.98);
  }
`;

export const FlagWrapper = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export const ButtonText = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#f8fafc' : '#1a1d1f')};
`;

export const Backdrop = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9998;
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  visibility: ${(props) => (props.$isOpen ? 'visible' : 'hidden')};
  transition: opacity 0.3s ease, visibility 0.3s ease;
`;

export const SlidePanel = styled.div<{ $isOpen: boolean; $isClosing: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: 100%;
  max-width: 450px;
  height: 100vh;
  background: ${({ theme }) => (theme.mode === 'dark' ? '#1a1f26' : '#ffffff')};
  box-shadow: ${({ theme }) => (theme.mode === 'dark'
    ? '-4px 0 24px rgba(255, 255, 255, 0.06)'
    : '-4px 0 24px rgba(224, 123, 45, 0.15)')};
  z-index: 9999;
  display: flex;
  flex-direction: column;
  animation: ${(props) => (props.$isClosing ? slideOut : slideIn)} 0.3s ease-out forwards;

  ${media.mobile} {
    max-width: 320px;
    border-radius: 6px 0 0 6px;
  }
`;

export const PanelHeader = styled.div`
  padding: 24px;
  border-bottom: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#e5e7eb')};
  flex-shrink: 0;

  ${media.mobile} {
    padding: 20px 16px;
  }
`;

export const PanelTitle = styled.h2`
  font-size: 20px;
  font-weight: 300;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#f8fafc' : '#1a1d1f')};
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 10px;

  ${media.mobile} {
    font-size: 18px;
    gap: 8px;
  }
`;

export const PanelDescription = styled.p`
  font-size: 14px;
  font-weight: 400;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#9ca3af' : '#6b7280')};
  line-height: 1.5;
  margin: 0;

  ${media.mobile} {
    font-size: 13px;
  }
`;

export const CloseButton = styled.button`
  position: absolute;
  top: 24px;
  right: 24px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#9ca3af' : '#6b7280')};
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#f3f4f6')};
    color: ${({ theme }) => (theme.mode === 'dark' ? '#f8fafc' : '#1a1d1f')};
  }

  &:active {
    transform: scale(0.95);
  }

  ${media.mobile} {
    top: 16px;
    right: 12px;
    width: 28px;
    height: 28px;
  }
`;

export const PanelBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;

  ${media.mobile} {
    padding: 16px;
    padding-bottom: 24px;
  }
`;

export const LanguageList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow: visible;

  ${media.mobile} {
    gap: 6px;
    padding-bottom: 20px;
  }
`;

export const DefaultLanguageBadge = styled.span`
  position: absolute;
  top: -8px;
  left: 8px;
  font-size: 9px;
  font-weight: 400;
  padding: 2px 6px;
  background: #1a4d4d;
  color: white;
  border-radius: 6px;
  text-transform: uppercase;
  white-space: nowrap;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  line-height: 1.3;
  z-index: 10;

  ${media.mobile} {
    font-size: 8px;
    padding: 2px 5px;
    top: -7px;
    left: 6px;
  }
`;

export const LanguageButton = styled.button<{ $isSelected?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 14px 16px;
  border: 1px solid ${({ $isSelected, theme }) => {
    if ($isSelected) return '#6bb2b2';
    return theme.mode === 'dark' ? '#374151' : '#e5e7eb';
  }};
  border-radius: 6px;
  background: ${({ $isSelected, theme }) => {
    if ($isSelected) return theme.mode === 'dark' ? 'rgba(224, 123, 45, 0.15)' : '#e6f2f2';
    return theme.mode === 'dark' ? '#242a33' : '#ffffff';
  }};
  cursor: pointer;
  transition: all 0.2s ease;
  overflow: visible;

  &:hover {
    background: ${({ $isSelected, theme }) => {
    if ($isSelected) return theme.mode === 'dark' ? 'rgba(224, 123, 45, 0.2)' : '#c0dede';
    return theme.mode === 'dark' ? '#374151' : '#f9fafb';
  }};
    border-color: ${({ $isSelected, theme }) => {
    if ($isSelected) return '#4d9e9e';
    return theme.mode === 'dark' ? '#4b5563' : '#d1d5db';
  }};
    transform: translateY(-1px);
    box-shadow: ${({ theme }) => (theme.mode === 'dark'
    ? '0 2px 8px rgba(255, 255, 255, 0.06)'
    : '0 2px 8px rgba(224, 123, 45, 0.1)')};
  }

  &:active {
    transform: translateY(0);
  }

  ${media.mobile} {
    padding: 12px;
    gap: 12px;
    border-radius: 6px;
  }
`;

export const LanguageFlagWrapper = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  ${media.mobile} {
    width: 28px;
    height: 28px;
    border-radius: 6px;
  }
`;

export const LanguageName = styled.span`
  font-size: 15px;
  font-weight: 500;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#f8fafc' : '#1a1d1f')};
  flex: 1;
  text-align: left;

  ${media.mobile} {
    font-size: 14px;
  }
`;

export const CheckIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #1a4d4d;
  flex-shrink: 0;

  ${media.mobile} {
    width: 20px;
    height: 20px;
  }
`;

export const PanelFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#e5e7eb')};
  background: ${({ theme }) => (theme.mode === 'dark' ? '#1a1f26' : '#f9fafb')};
  flex-shrink: 0;

  ${media.mobile} {
    padding: 12px 16px;
  }
`;

export const FooterText = styled.p`
  font-size: 13px;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#9ca3af' : '#6b7280')};
  line-height: 1.5;
  margin: 0;

  ${media.mobile} {
    font-size: 12px;
  }
`;

export const FooterLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 13px;
  font-weight: 500;
  color: #1a4d4d;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
  transition: color 0.2s ease;

  &:hover {
    color: #0f3333;
  }

  ${media.mobile} {
    font-size: 12px;
  }
`;
